/**
 * DeltaChecker - Verificación de cambios en fuentes de datos
 * 
 * @description Compara ETag/checksum/TTL para detectar actualizaciones
 * @version 1.0.0
 * @task P1.09 - Plan Sistemático v4.2
 */

import type { 
  SourceConfig, 
  SourceName,
  SourceType,
  DeltaCheckResult,
  DeltaCheckReason as _DeltaCheckReason,
} from './SyncManifest';

import { isSourceExpired } from './SyncManifest';

import { WORKER_BASE } from '@/lib/config/workerEndpoints';

// ═══════════════════════════════════════════════════════════════════════════
// CONFIGURACIÓN
// ═══════════════════════════════════════════════════════════════════════════

/**
 * URL base del Worker R2 (alias local).
 *
 * Fuente única de verdad: `@/lib/config/workerEndpoints` (`WORKER_BASE`).
 * Se preserva el nombre interno `R2_WORKER_URL` para minimizar churn.
 */
const R2_WORKER_URL = WORKER_BASE;

/** Timeout para peticiones HEAD (ms) */
const HEAD_TIMEOUT = 5000;

/** Timeout para peticiones de contenido (ms) */
const CONTENT_TIMEOUT = 30000;

/** Mapeo de fuentes a rutas R2 */
const R2_PATHS: Record<SourceName, string> = {
  catastro: 'catastro/meta.json',
  dera: 'dera/all-dera.json.gz',
  nga: 'nga/nga-all.json.gz',
  ine: 'ine/municipios.json.gz',
  cdau: 'cdau/cdau-all.json.gz',
  sigpac: 'sigpac/sigpac-all.json.gz'
};

// ═══════════════════════════════════════════════════════════════════════════
// TIPOS
// ═══════════════════════════════════════════════════════════════════════════

/** Metadatos de recurso remoto */
export interface RemoteResourceMeta {
  etag: string | null;
  lastModified: string | null;
  contentLength: number | null;
  exists: boolean;
}

/** Estado local de una fuente */
export interface LocalSourceState {
  sourceName: SourceName;
  etag?: string;
  checksum?: string;
  lastSync?: string;
  recordCount?: number;
}

/** Resultado de comparación de checksum */
export interface ChecksumComparison {
  match: boolean;
  localChecksum: string | null;
  remoteChecksum: string | null;
  algorithm: 'sha256';
}

// ═══════════════════════════════════════════════════════════════════════════
// UTILIDADES CRYPTO
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Calcula SHA-256 de un ArrayBuffer o string
 */
export async function calculateSHA256(data: ArrayBuffer | string): Promise<string> {
  let buffer: ArrayBuffer;
  
  if (typeof data === 'string') {
    const encoder = new TextEncoder();
    buffer = encoder.encode(data).buffer;
  } else {
    buffer = data;
  }
  
  // Wrap en Uint8Array para garantizar TypedArray nativa en todos los
  // entornos (jsdom devuelve Node Buffer desde Blob.arrayBuffer() que
  // no pasa la comprobación interna de SubtleCrypto)
  const hashBuffer = await crypto.subtle.digest('SHA-256', new Uint8Array(buffer));
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  
  return hashHex;
}

/**
 * Calcula SHA-256 de un Blob/File
 */
export async function calculateBlobSHA256(blob: Blob): Promise<string> {
  const raw = await blob.arrayBuffer();
  // jsdom 28/29 devuelve Uint8Array en lugar de ArrayBuffer nativo — normalizar
  const buffer: ArrayBuffer = raw instanceof ArrayBuffer
    ? raw
    : (raw as unknown as { buffer: ArrayBuffer }).buffer;
  return calculateSHA256(buffer);
}

// ═══════════════════════════════════════════════════════════════════════════
// VERIFICACIÓN R2
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Obtiene metadatos de recurso R2 via HEAD request
 */
async function fetchR2Metadata(path: string): Promise<RemoteResourceMeta> {
  const url = `${R2_WORKER_URL}/${path}`;
  
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), HEAD_TIMEOUT);
    
    const response = await fetch(url, {
      method: 'HEAD',
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      return { etag: null, lastModified: null, contentLength: null, exists: false };
    }
    
    return {
      etag: response.headers.get('etag'),
      lastModified: response.headers.get('last-modified'),
      contentLength: parseInt(response.headers.get('content-length') || '0', 10) || null,
      exists: true
    };
    
  } catch {
    return { etag: null, lastModified: null, contentLength: null, exists: false };
  }
}

/**
 * Verifica si una fuente R2 tiene cambios
 */
export async function checkR2Source(
  sourceName: SourceName,
  sourceConfig: SourceConfig,
  localState: LocalSourceState | null
): Promise<DeltaCheckResult> {
  const now = new Date().toISOString();
  
  // 1. Verificar TTL primero
  if (isSourceExpired(sourceConfig)) {
    return {
      sourceName,
      hasChanges: true,
      reason: 'ttl_expired',
      message: `TTL de ${sourceConfig.ttlSeconds}s expirado`,
      checkedAt: now
    };
  }
  
  // 2. Si no hay estado local, necesita sync inicial
  if (!localState) {
    return {
      sourceName,
      hasChanges: true,
      reason: 'first_sync',
      message: 'Sin estado local, requiere sincronización inicial',
      checkedAt: now
    };
  }
  
  // 3. Obtener ruta R2
  const r2Path = R2_PATHS[sourceName];
  if (!r2Path) {
    return {
      sourceName,
      hasChanges: false,
      reason: 'error',
      message: `Fuente ${sourceName} sin ruta R2 configurada`,
      checkedAt: now
    };
  }
  
  const remoteMeta = await fetchR2Metadata(r2Path);
  
  if (!remoteMeta.exists) {
    return {
      sourceName,
      hasChanges: false,
      reason: 'error',
      message: `Recurso no encontrado en R2: ${r2Path}`,
      checkedAt: now
    };
  }
  
  // 4. Comparar ETag si disponible
  if (remoteMeta.etag && localState.etag) {
    if (remoteMeta.etag !== localState.etag) {
      return {
        sourceName,
        hasChanges: true,
        reason: 'etag_mismatch',
        previousEtag: localState.etag,
        newEtag: remoteMeta.etag,
        message: `ETag cambió: ${localState.etag.slice(0, 8)}... → ${remoteMeta.etag.slice(0, 8)}...`,
        checkedAt: now
      };
    }
  }
  
  // 5. Comparar hash si disponible
  if (sourceConfig.contentHash && localState.checksum) {
    if (sourceConfig.contentHash !== localState.checksum) {
      return {
        sourceName,
        hasChanges: true,
        reason: 'hash_mismatch',
        previousHash: localState.checksum,
        newHash: sourceConfig.contentHash,
        message: 'Hash de contenido cambió',
        checkedAt: now
      };
    }
  }
  
  // 6. Sin cambios detectados
  return {
    sourceName,
    hasChanges: false,
    reason: 'no_changes',
    message: 'Sin cambios detectados (ETag/hash coinciden)',
    checkedAt: now
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// VERIFICACIÓN WFS/API
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Verifica si una fuente WFS/API tiene cambios
 * Para fuentes externas, principalmente usamos TTL
 */
export async function checkWFSSource(
  sourceName: SourceName,
  sourceConfig: SourceConfig,
  localState: LocalSourceState | null
): Promise<DeltaCheckResult> {
  const now = new Date().toISOString();
  
  // 1. Verificar TTL
  if (isSourceExpired(sourceConfig)) {
    return {
      sourceName,
      hasChanges: true,
      reason: 'ttl_expired',
      message: `TTL de ${sourceConfig.ttlSeconds}s expirado para WFS/API`,
      checkedAt: now
    };
  }
  
  // 2. Si no hay estado local, necesita sync
  if (!localState) {
    return {
      sourceName,
      hasChanges: true,
      reason: 'first_sync',
      message: 'Sin estado local, requiere sincronización inicial',
      checkedAt: now
    };
  }
  
  // 3. Comparar ETag del manifest si disponible
  if (sourceConfig.etag && localState.etag) {
    if (sourceConfig.etag !== localState.etag) {
      return {
        sourceName,
        hasChanges: true,
        reason: 'etag_mismatch',
        previousEtag: localState.etag,
        newEtag: sourceConfig.etag,
        message: 'ETag del manifest cambió',
        checkedAt: now
      };
    }
  }
  
  // 4. WFS con TTL activo = sin cambios
  return {
    sourceName,
    hasChanges: false,
    reason: 'no_changes',
    message: `Dentro de TTL (${sourceConfig.ttlSeconds}s)`,
    checkedAt: now
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// VERIFICACIÓN POR CHECKSUM
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Compara checksum local con remoto
 */
export async function compareChecksum(
  url: string,
  localChecksum: string | null
): Promise<ChecksumComparison> {
  if (!localChecksum) {
    return { match: false, localChecksum: null, remoteChecksum: null, algorithm: 'sha256' };
  }
  
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), CONTENT_TIMEOUT);
    
    const response = await fetch(url, { signal: controller.signal });
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      return { match: false, localChecksum, remoteChecksum: null, algorithm: 'sha256' };
    }
    
    const buffer = await response.arrayBuffer();
    const remoteChecksum = await calculateSHA256(buffer);
    
    return {
      match: localChecksum === remoteChecksum,
      localChecksum,
      remoteChecksum,
      algorithm: 'sha256'
    };
    
  } catch {
    return { match: false, localChecksum, remoteChecksum: null, algorithm: 'sha256' };
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// CLASE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

/**
 * DeltaChecker - Verificador de cambios en fuentes
 */
export class DeltaChecker {
  private workerUrl: string;
  
  constructor(workerUrl: string = R2_WORKER_URL) {
    this.workerUrl = workerUrl;
  }
  
  /** Verifica fuente R2 */
  async checkR2(
    sourceName: SourceName,
    sourceConfig: SourceConfig,
    localState: LocalSourceState | null
  ): Promise<DeltaCheckResult> {
    return checkR2Source(sourceName, sourceConfig, localState);
  }
  
  /** Verifica fuente WFS/API */
  async checkWFS(
    sourceName: SourceName,
    sourceConfig: SourceConfig,
    localState: LocalSourceState | null
  ): Promise<DeltaCheckResult> {
    return checkWFSSource(sourceName, sourceConfig, localState);
  }
  
  /** Verifica fuente según su tipo */
  async check(
    sourceName: SourceName,
    sourceConfig: SourceConfig,
    localState: LocalSourceState | null
  ): Promise<DeltaCheckResult> {
    const sourceType: SourceType = sourceConfig.type;
    
    switch (sourceType) {
      case 'r2-static':
        return this.checkR2(sourceName, sourceConfig, localState);
      
      case 'wfs-api':
      case 'http-api':
        return this.checkWFS(sourceName, sourceConfig, localState);
      
      default:
        return {
          sourceName,
          hasChanges: false,
          reason: 'error',
          message: `Tipo de fuente no soportado: ${sourceType}`,
          checkedAt: new Date().toISOString()
        };
    }
  }
  
  /** Calcula SHA-256 */
  async sha256(data: ArrayBuffer | string): Promise<string> {
    return calculateSHA256(data);
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// SINGLETON
// ═══════════════════════════════════════════════════════════════════════════

let defaultChecker: DeltaChecker | null = null;

/** Obtiene instancia singleton de DeltaChecker */
export function getDeltaChecker(): DeltaChecker {
  if (!defaultChecker) {
    defaultChecker = new DeltaChecker();
  }
  return defaultChecker;
}
